import { formatDate } from "@utils/utils";
import expressionParser from "docxtemplater/expressions";

import {
  ExportClientDetailsType,
  ExportGoalType,
  ExportSectionFieldType,
  ExportSignatureSectionType,
  Maybe,
  ReportExportDataType,
} from "@api/graphql/types-and-hooks";
import { getCheckboxFieldAnswer, getRadioAndSelectFieldAnswer } from "@components/FieldComponent/utils";
import { ReportFieldIdentifier, ReportSectionIdentifier } from "@components/PDFGenerator/Catalight/CatalightTypes";
import {
  Dsm5Row,
  DsmSpecifierRow,
  TransformedData,
  getImageContentBase64FromField,
  getSectionObjectBySectionIdentifier,
} from "@components/PDFGenerator/Catalight/catalightUtils";
import {
  OTHER_SETTING_LOCATION,
  PractitionerDataRow,
} from "@components/ReportField/inputs/ReportFieldTreatmentOptionTable";
import { ReportNode } from "@providers/ReportProvider";

type ReportSectionFieldEdge = NonNullable<
  NonNullable<NonNullable<NonNullable<ReportNode["reportSections"]>["edges"][number]>["node"]>["sectionField"]
>["edges"][number];

type ReportGoalNode = NonNullable<
  NonNullable<NonNullable<NonNullable<ReportNode["reportTreatmentPlan"]>["reportgoalSet"]>["edges"][number]>["node"]
>;

interface ImageData {
  data: ArrayBuffer;
  size: [number, number];
}

type TableData = {
  [key: string]: string | number | boolean | undefined;
};

interface ExportSectionField {
  title: string;
  content: string | TableData[] | boolean | null | TransformedData | DsmSpecifierRow[];
  field_type: string;
  field_identifier: string;
  /** Original column label (e.g. "95% Confidence Interval") for TABLE fields whose
   * Confidence_Interval column was normalized by the backend's key sanitizer. */
  confidence_interval_label?: string;
}

interface ExportSection {
  title: string;
  fields: ExportSectionField[];
}

export type TemplateDataValue =
  | string
  | ExportSection
  | TableData[]
  | ReportGoalNode[]
  | ExportGoalType[]
  | DsmSpecifierRow[]
  | ImageData
  | FieldMapValue
  | null
  | undefined
  | TransformedData;

type FieldMapValue = string | number | boolean | Buffer | null | undefined | TransformedData[];
export interface FieldMap {
  [key: string]: FieldMapValue;
}

export interface ImageDimensions {
  width: number;
  height: number;
}

export const markdownToDocxtemplater = (markdown: string, font: string = "Public Sans") => {
  const fontStyle = () => {
    return `<w:rFonts w:ascii="${font}" w:hAnsi="${font}" w:cs="${font}"/>`;
  };

  // Escape XML special characters
  const escapeXml = (unsafe: string) => {
    return unsafe.replace(/[<>&'"]/g, (c) => {
      switch (c) {
        case "<":
          return "&lt;";
        case ">":
          return "&gt;";
        case "&":
          return "&amp;";
        case "'":
          return "&apos;";
        case '"':
          return "&quot;";
      }
      return c;
    });
  };

  const convertHeaders = (text: string) => {
    return text
      .replace(/^#\s+(.+)$/gm, (_match, content) => {
        return `<w:p><w:pPr>${fontStyle()}<w:pStyle w:val="Heading1"/></w:pPr>${convertInlineStyles(content)}</w:p>`;
      })
      .replace(/^##\s+(.+)$/gm, (_match, content) => {
        return `<w:p><w:pPr>${fontStyle()}<w:pStyle w:val="Heading2"/></w:pPr>${convertInlineStyles(content)}</w:p>`;
      })
      .replace(/^###\s+(.+)$/gm, (_match, content) => {
        return `<w:p><w:pPr>${fontStyle()}<w:pStyle w:val="Heading3"/></w:pPr>${convertInlineStyles(content)}</w:p>`;
      })
      .replace(/^####\s+(.+)$/gm, (_match, content) => {
        return `<w:p><w:pPr>${fontStyle()}<w:pStyle w:val="Heading4"/></w:pPr>${convertInlineStyles(content)}</w:p>`;
      });
  };

  // Allowlist of schemes safe to embed in a docx HYPERLINK. Anything else
  // (javascript:, data:, vbscript:, file:, custom protocols) is dropped to
  // text-only — defense in depth against malicious WYSIWYG content.
  const SAFE_LINK_SCHEMES = ["http:", "https:", "mailto:", "tel:"];

  // Returns a normalized URL safe to embed in the HYPERLINK field, or null
  // if the URL is unsafe and the caller should render text without a hyperlink.
  const normalizeLinkUrl = (url: string): string | null => {
    const trimmed = url.trim();
    const lower = trimmed.toLowerCase();

    if (trimmed.startsWith("#")) return trimmed;
    if (trimmed.startsWith("//")) return `https:${trimmed}`;
    if (SAFE_LINK_SCHEMES.some((s) => lower.startsWith(s))) return trimmed;
    // Reject any other explicit scheme (javascript:, data:, etc.)
    if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return null;
    if (/^www\./i.test(trimmed)) return `https://${trimmed}`;
    // Relative paths / absolute paths — leave as-is; Word resolves them itself.
    return trimmed;
  };

  // Schema-defined child order inside <w:rPr> for the inline-style elements
  // we emit. Used to avoid violating CT_RPr ordering when nesting styles.
  const RPR_STYLE_ORDER = ["b", "i", "strike", "u"] as const;
  const orderedStyleTags = (styles: readonly string[]) =>
    RPR_STYLE_ORDER.filter((s) => styles.includes(s))
      .map((s) => `<w:${s}/>`)
      .join("");

  const renderTextRun = (text: string, styles: readonly string[]) => {
    return `<w:r><w:rPr>${fontStyle()}${orderedStyleTags(styles)}</w:rPr><w:t xml:space="preserve">${escapeXml(text)}</w:t></w:r>`;
  };

  // Field-based HYPERLINK avoids needing a relationship entry in the docx package.
  // `parentStyles` lets a wrapping bold/italic/strike propagate into the link's
  // visible run (e.g. `**[link](url)**`).
  const buildHyperlinkField = (text: string, normalizedUrl: string, parentStyles: readonly string[] = []) => {
    // Drop `u` from parent styles — the hyperlink run already carries underline.
    const extraStyles = orderedStyleTags(parentStyles.filter((s) => s !== "u"));
    return (
      `<w:r><w:fldChar w:fldCharType="begin"/></w:r>` +
      `<w:r><w:instrText xml:space="preserve"> HYPERLINK "${escapeXml(normalizedUrl)}" </w:instrText></w:r>` +
      `<w:r><w:fldChar w:fldCharType="separate"/></w:r>` +
      `<w:r><w:rPr><w:rStyle w:val="Hyperlink"/>${fontStyle()}${extraStyles}<w:color w:val="0000FF"/><w:u w:val="single"/></w:rPr>` +
      `<w:t xml:space="preserve">${escapeXml(text)}</w:t></w:r>` +
      `<w:r><w:fldChar w:fldCharType="end"/></w:r>`
    );
  };

  // Decode the HTML entities Quill commonly emits in attribute values and text
  // (mainly `&amp;` in URLs). Caller is responsible for re-escaping for XML.
  const decodeHtmlEntities = (s: string) =>
    s
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&apos;/g, "'")
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, "&");

  // Split a paragraph's HTML content into a sequence of OOXML runs, turning
  // `<a href="...">text</a>` into proper hyperlink fields. Other inline tags
  // (`<strong>`, `<em>`, etc.) are not handled here — pre-existing limitation.
  const renderHtmlInlineContent = (content: string, parentStyles: readonly string[] = []): string => {
    const anchorRegex = /<a\s+[^>]*?\bhref="([^"]*)"[^>]*>([\s\S]*?)<\/a>/gi;
    const runs: string[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = anchorRegex.exec(content)) !== null) {
      if (match.index > lastIndex) {
        runs.push(renderTextRun(decodeHtmlEntities(content.slice(lastIndex, match.index)), parentStyles));
      }
      const url = decodeHtmlEntities(match[1]);
      const text = decodeHtmlEntities(match[2]);
      const normalizedUrl = normalizeLinkUrl(url);
      if (normalizedUrl === null) {
        runs.push(renderTextRun(text, parentStyles));
      } else {
        runs.push(buildHyperlinkField(text, normalizedUrl, parentStyles));
      }
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < content.length) {
      runs.push(renderTextRun(decodeHtmlEntities(content.slice(lastIndex)), parentStyles));
    }
    return runs.join("");
  };

  const convertHtmlToOoxml = (html: string) => {
    return (
      html
        // Whole-paragraph bold: <p><strong>Text</strong></p>
        .replace(/<p><strong>(.*?)<\/strong><\/p>/g, (_match, content) => {
          if (content.trim() === "") {
            return '<w:p><w:r><w:t xml:space="preserve"> </w:t></w:r></w:p>';
          }
          return `<w:p>${renderHtmlInlineContent(content, ["b"])}</w:p>`;
        })
        // Empty paragraphs
        .replace(/<p><\/p>/g, '<w:p><w:r><w:t xml:space="preserve"> </w:t></w:r></w:p>')
        // Remaining paragraphs
        .replace(/<p>(.*?)<\/p>/g, (_match, content) => {
          if (content.trim() === "") {
            return '<w:p><w:r><w:t xml:space="preserve"> </w:t></w:r></w:p>';
          }
          return `<w:p>${renderHtmlInlineContent(content)}</w:p>`;
        })
    );
  };

  // Convert inline styles (links, bold, italic, underline, strikethrough).
  // `parentStyles` accumulates wrapping styles when recursing into a matched
  // styled segment, so e.g. `**[link](url)**` carries bold into the hyperlink.
  const convertInlineStyles = (text: string, parentStyles: readonly string[] = []): string => {
    const segments: Array<{ text: string; style: string | null | undefined; url?: string }> = [];
    let currentIndex = 0;

    const patterns = [
      { regex: /(?<!!)\[([^\]]+)\]\(([^)\s]+)\)/g, tag: "link" }, // Markdown link (excludes ![alt](url) images)
      { regex: /\*\*(.+?)\*\*/g, tag: "b" }, // Bold
      { regex: /\*(.+?)\*/g, tag: "i" }, // Italic
      { regex: /__(.+?)__/g, tag: "u" }, // Underline
      { regex: /~(.+?)~/g, tag: "strike" }, // Strikethrough
    ];

    while (currentIndex < text.length) {
      let match: RegExpExecArray | null = null;
      let earliestIndex = text.length;
      let matchedPattern: { regex: RegExp; tag: string } | null = null;

      for (const pattern of patterns) {
        pattern.regex.lastIndex = currentIndex;
        const m = pattern.regex.exec(text);
        if (m && m.index < earliestIndex) {
          match = m;
          earliestIndex = m.index;
          matchedPattern = pattern;
        }
      }

      if (match) {
        if (match.index > currentIndex) {
          segments.push({ text: text.slice(currentIndex, match.index), style: null });
        }
        if (matchedPattern?.tag === "link") {
          segments.push({ text: match[1], style: "link", url: match[2] });
        } else {
          segments.push({ text: match[1], style: matchedPattern?.tag });
        }
        currentIndex = match.index + match[0].length;
      } else {
        segments.push({ text: text.slice(currentIndex), style: null });
        break;
      }
    }

    // Convert segments to OOXML format
    return segments
      .map(({ text, style, url }) => {
        if (style === "link" && url) {
          const normalizedUrl = normalizeLinkUrl(url);
          if (normalizedUrl === null) {
            // Unsafe scheme (javascript:, data:, etc.) — render as plain text, no hyperlink.
            return renderTextRun(text, parentStyles);
          }
          return buildHyperlinkField(text, normalizedUrl, parentStyles);
        }
        if (!style) {
          return renderTextRun(text, parentStyles);
        }
        // Recurse so an inner link inside a styled segment (e.g. `**[link](url)**`)
        // still gets parsed as a link with the outer styles applied.
        return convertInlineStyles(text, [...parentStyles, style]);
      })
      .join("");
  };

  const convertBullets = (text: string) => {
    return text.replace(/^(\s*)\*\s+(.*)$/gm, (_match, indent, content) => {
      const level = Math.floor(indent.length / 2);
      const formattedContent = convertInlineStyles(content); // Process inline styles

      // Calculate indentation based on level (360 TWIPs = 0.25 inches per level)
      const leftIndent = 360 + level * 360;
      const hangingIndent = 360;

      return (
        "<w:p>" +
        "<w:pPr>" +
        '<w:pStyle w:val="ListParagraph"/>' +
        `<w:ind w:left="${leftIndent}" w:hanging="${hangingIndent}"/>` +
        `<w:keepLines/>` + // Prevents line breaking
        `<w:keepNext/>` + // Keeps bullet and content together
        "</w:pPr>" +
        "<w:r>" +
        `<w:rPr>${fontStyle()}</w:rPr>` +
        '<w:t xml:space="preserve">\u2022  </w:t>' + // Unicode bullet + spaces
        "</w:r>" +
        formattedContent +
        "</w:p>"
      );
    });
  };

  const convertParagraphs = (text: string) => {
    return text
      .replace(/\u00A0/g, " ") // Replace non-breaking spaces
      .split("\n")
      .map((line) => {
        if (line.trim() === "") return '<w:p><w:r><w:t xml:space="preserve"> </w:t></w:r></w:p>'; // Convert empty lines to empty paragraphs
        if (line.startsWith("<w:p>")) return line; // Already converted (e.g., headers, bullets)
        return `<w:p>${convertInlineStyles(line)}</w:p>`;
      })
      .join("");
  };

  const convertLineBreaks = (text: string) => {
    return text.replace(/&nbsp;/g, "");
  };

  const convertAmpersands = (text: string) => {
    // Skip ampersands inside markdown link URLs/text so query strings survive intact;
    // escapeXml handles the literal `&` correctly in the OOXML output.
    return text.replace(/(?<!!)\[[^\]]+\]\([^)\s]+\)|&/g, (match) => (match === "&" ? "and" : match));
  };

  if (markdown.trim().startsWith("<p>")) {
    return convertHtmlToOoxml(markdown);
  }

  let docxText = markdown;
  docxText = convertLineBreaks(docxText);
  docxText = convertAmpersands(docxText);
  docxText = convertHeaders(docxText);
  docxText = convertBullets(docxText);
  docxText = convertParagraphs(docxText);

  return docxText;
};

/**
 * Verifies that an object has no duplicate keys
 * @param data The object to check for duplicates
 * @returns True if there are no duplicates, throws an error if duplicates are found
 */
export const verifyUniqueFieldMapKeys = <T extends object>(data: T): boolean => {
  const duplicateKeys: string[] = [];
  const seenKeys = new Set<string>();

  Object.keys(data as Record<string, unknown>).forEach((key) => {
    if (seenKeys.has(key)) {
      duplicateKeys.push(key);
    } else {
      seenKeys.add(key);
    }
  });

  if (duplicateKeys.length > 0) {
    console.error(`Duplicate keys found in data object: ${duplicateKeys.join(", ")}`);
    throw new Error(`Duplicate keys found in data object: ${duplicateKeys.join(", ")}`);
  }

  return true;
};

interface DocxTagParser {
  get(scope: unknown, context?: unknown): unknown;
}

// angular-expressions (the engine behind docxtemplater's expression parser) compiles
// tags with `new Function(...)` by default, which our CSP blocks ('unsafe-eval' not
// allowed). `csp: true` switches it to a pure-JS interpreter instead.
const cspSafeExpressionParser = expressionParser.configure({ csp: true });

/**
 * cspSafeExpressionParser still compiles every tag as a JS/Angular expression. Tags
 * that predate the expression parser (e.g. legacy "~~content" WYSIWYG markers) aren't
 * valid expression syntax and throw a ScopeParserError — Docxtemplater's own default
 * parser (no `parser` option) instead does a plain `scope[tag]` lookup for these. Fall
 * back to that lookup instead of failing the entire render when a tag doesn't compile.
 */
export const safeExpressionParser = (tag: string): DocxTagParser => {
  try {
    return cspSafeExpressionParser(tag);
  } catch {
    return {
      get: (scope: unknown) => (scope as Record<string, unknown> | null | undefined)?.[tag],
    };
  }
};

/**
 * Preprocesses a ReportGoalType object to replace undefined string values with empty strings
 * @param goal The ReportGoalType object to preprocess
 * @returns A new ReportGoalType object with undefined values replaced with empty strings
 */
export const preprocessShortTermGoalNode = <T extends Partial<ReportGoalNode>>(goal: T): T => {
  return {
    ...goal,
    description: goal.description ?? "",
    assessmentToolSource: goal.assessmentToolSource ?? "",
    baselineDate: goal.baselineDate ? formatDate(goal.baselineDate) : "",
    baselineData: goal.baselineData ?? "",
    masteryCriteria: goal.masteryCriteria ?? "",
    medicalNecessityCriteria: goal.medicalNecessityCriteria ?? "",
    programGoal: goal.programGoal ?? "",
    generalizationCriteria: goal.generalizationCriteria ?? "",
    rationale: goal.rationale ?? "",
  };
};

// https://github.com/evilc0des/docxtemplater-image-module-free?tab=readme-ov-file#base64-include
/**
 * Converts a base64-encoded image string to an ArrayBuffer.
 *
 * The input string must be a valid data URL for an image (jpeg, jpg, png, gif, bmp, webp, or svg+xml).
 * If the format is invalid, the function logs a warning and returns null.
 *
 * @param base64String - The base64-encoded image string in data URL format.
 * @returns The decoded image as an ArrayBuffer, or null if the input format is invalid.
 */
export const base64StringToArrayBuffer = (base64String: string): ArrayBuffer | null => {
  const base64Regex = /^data:image\/(jpeg|jpg|png|gif|bmp|webp|svg\+xml);base64,/;
  if (!base64Regex.test(base64String)) {
    console.warn(`base64StringToArrayBuffer: Invalid format for "${base64String}"`);
    return null;
  }
  const stringBase64 = base64String.replace(base64Regex, "");
  const binaryString = atob(stringBase64);

  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    const ascii = binaryString.charCodeAt(i);
    bytes[i] = ascii;
  }
  return bytes.buffer;
};

/**
 * Fetches an image from a URL and converts it to a base64 data URL.
 * Used for converting logo URLs to base64 for docxtemplater image placeholders.
 * Uses fetch API to avoid CORS issues with pre-signed S3 URLs.
 *
 * @param url - The URL of the image to fetch (typically a pre-signed S3 URL)
 * @returns Promise resolving to a base64 data URL string, or null if the fetch fails
 */
export const fetchImageAsBase64 = async (url: string): Promise<string | null> => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      return null;
    }

    const blob = await response.blob();

    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

/**
 * Determines the appropriate header logo URL based on organization settings.
 * If the organization is allowed to use their own logo and has one set, use it.
 * Otherwise, fall back to the template logo.
 *
 * @param organizationLogoAllowed - Whether the organization can override the template logo
 * @param organizationLogoSignedUrl - The organization's custom logo URL
 * @param templateLogoSignedUrl - The default template logo URL
 * @returns The selected logo URL, or null if no logo is available
 */
export const selectHeaderLogoUrl = (
  organizationLogoAllowed: boolean | null | undefined,
  organizationLogoSignedUrl: string | null | undefined,
  templateLogoSignedUrl: string | null | undefined,
): string | null => {
  if (organizationLogoAllowed) {
    return organizationLogoSignedUrl || templateLogoSignedUrl || null;
  }
  return templateLogoSignedUrl || null;
};

/**
 * Adjusts the dimensions of an image to fit within specified maximum and minimum bounds,
 * preserving the aspect ratio. If the image is smaller than the minimum width, it is scaled up.
 * If it exceeds the maximum width or height, it is scaled down accordingly.
 *
 * @param width - The original width of the image in pixels.
 * @param height - The original height of the image in pixels.
 * @returns An object containing the adjusted width and height that fit within the defined constraints.
 */
const adjustImageDimensions = (width: number, height: number): ImageDimensions => {
  // Content area of a Letter page with 1in margins at 96 DPI.
  const maxWidth = 624;
  const maxHeight = 864;

  let finalWidth = width;
  let finalHeight = height;

  // Scale up if image is too small (minimum 300px width)
  const minWidth = 300;
  if (finalWidth < minWidth) {
    const scale = minWidth / finalWidth;
    finalWidth = minWidth;
    finalHeight = Math.round(finalHeight * scale);
  }

  // Scale down if too large
  if (finalWidth > maxWidth) {
    const scale = maxWidth / finalWidth;
    finalWidth = maxWidth;
    finalHeight = Math.round(finalHeight * scale);
  }

  if (finalHeight > maxHeight) {
    const scale = maxHeight / finalHeight;
    finalHeight = maxHeight;
    finalWidth = Math.round(finalWidth * scale);
  }

  return { width: finalWidth, height: finalHeight };
};

/**
 * Helper to load an image from a base64 string and return its adjusted dimensions.
 * @param base64 - The base64 image string
 * @returns Promise resolving to ImageDimensions or undefined if not an image
 */
const getImageDimensionsFromBase64 = async (base64: string): Promise<ImageDimensions | undefined> => {
  if (typeof base64 === "string" && base64.startsWith("data:image/")) {
    try {
      const img = new Image();
      return await new Promise<ImageDimensions | undefined>((resolve) => {
        img.onload = () => {
          resolve(adjustImageDimensions(img.width, img.height));
        };
        img.onerror = () => resolve(undefined);
        img.src = base64;
      });
    } catch (error) {
      console.error(`Error resolving image sizes: ${error}`);
    }
  }
  return undefined;
};

/**
 * Returns a record of image dimensions for base64-encoded images in the template data.
 * Loads each image and adjusts its size.
 *
 * @param templateData - Object with possible base64 image values.
 * @returns Promise resolving to a record of image keys and their dimensions.
 */
export const resolveImageDimensions = async (
  templateData: Record<string, TemplateDataValue>,
): Promise<Record<string, ImageDimensions>> => {
  const imageDimensions: Record<string, ImageDimensions> = {};
  for (const [key, value] of Object.entries(templateData)) {
    if (typeof value === "string" && value.startsWith("data:image/")) {
      const dimensions = await getImageDimensionsFromBase64(value);
      if (dimensions) {
        imageDimensions[key] = dimensions;
      }
    } else if (value && typeof value === "object" && "title" in value && "fields" in value) {
      const section = value as ExportSection;
      for (const field of Object.values(section.fields)) {
        if (
          field.field_type === "IMAGE" &&
          typeof field.content === "string" &&
          field.content.startsWith("data:image/")
        ) {
          const dimensions = await getImageDimensionsFromBase64(field.content);
          if (dimensions) {
            imageDimensions[key] = dimensions;
          }
        }
      }
    }
  }
  return imageDimensions;
};

/** Reads the Confidence_Interval_Label the backend stashes on each row when it
 * normalizes a "<N>% Confidence Interval" column (see PROA-2109) — every row
 * carries the same label, so the first row is enough to label the column. */
export const getConfidenceIntervalLabel = (content: ExportSectionField["content"]): string | undefined => {
  if (!Array.isArray(content)) return undefined;
  const label = (content[0] as TableData | undefined)?.Confidence_Interval_Label;
  return typeof label === "string" ? label : undefined;
};

const transformTableContent = (content: string): TableData[] | null => {
  if (!content) return null;
  try {
    const json = JSON.parse(content);

    // handle table data if it is already an array
    if (Array.isArray(json)) {
      return json as TableData[];
    }

    const tableData = json["data"];
    if (!tableData || !Array.isArray(tableData)) {
      throw new Error("Invalid JSON format. An array is expected.");
    }
    return tableData as TableData[];
  } catch (error) {
    console.error("Error parsing JSON:", error);
    return null;
  }
};

/**
 * adds a "isA1: true" type field to each row so the table can be used dynamically within the docx template
 * @param tableContent
 * @returns the transformed table data with the additional boolean keys
 */
const transformDSMTableContent = (content: string): TransformedData[] => {
  const tableContent = (transformTableContent(content) as Dsm5Row[]) || [];
  return tableContent.map((tableRow: Dsm5Row) => ({
    ...tableRow,
    [`is${tableRow.id}`]: true,
  }));
};

export const transformTreatmentTableData = (content: string | null, keyPrefix: string): TransformedData => {
  const levels = ["Paraprofessional", "Social Skills Group", "Mid-Level Supervisor", "High Level Supervisor"];
  const knownLocations = ["Home", "Clinic/Center", "Community", "Telehealth", "Other Setting"];

  const sanitizeKey = (str: string) =>
    str
      .replace(/[^a-zA-Z0-9\s]+/g, "")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join("");

  // Precompute sanitized level and location keys
  const levelKeys = levels.reduce(
    (acc, level) => {
      acc[level] = sanitizeKey(level);
      return acc;
    },
    {} as Record<string, string>,
  );

  const locationKeys = knownLocations.reduce(
    (acc, location) => {
      acc[location] = sanitizeKey(location);
      return acc;
    },
    {} as Record<string, string>,
  );

  const formatValue = (value: number | null, isExectHourValue: boolean = false) => {
    if (value == null) {
      return isExectHourValue ? "__ Hour/Month" : "__";
    }

    if (isExectHourValue) {
      const unit = value < 2 ? "Hour/Month" : "Hours/Month";
      return `${value} ${unit}`;
    }

    return `${value}`;
  };

  // Initialize all possible keys with default values
  const result: TransformedData = {};
  for (const level of levels) {
    const levelKey = levelKeys[level];
    result[`${keyPrefix}${levelKey}Hours`] = "__";
    result[`${keyPrefix}${levelKey}HourRangeMin`] = "__";
    result[`${keyPrefix}${levelKey}HourRangeMax`] = "__";
    result[`${keyPrefix}${levelKey}IsRange`] = false;
    result[`${keyPrefix}${levelKey}HasSecondRange`] = false;
    result[`${keyPrefix}${levelKey}SecondHourRangeMin`] = "__";
    result[`${keyPrefix}${levelKey}SecondHourRangeMax`] = "__";
    result[`${keyPrefix}${levelKey}OtherSettingDescription`] = "";

    for (const location of knownLocations) {
      const locationKey = locationKeys[location];
      result[`${keyPrefix}${levelKey}ServiceLocation${locationKey}`] = false;
    }
  }

  if (!content || !content.length) {
    return result;
  }

  try {
    const parsed: PractitionerDataRow[] = JSON.parse(content);

    for (const item of parsed) {
      const levelKey = levelKeys[item.level];
      if (!levelKey) continue; // skip unknown levels safely

      if (item.isRange) {
        result[`${keyPrefix}${levelKey}IsRange`] = true;
        result[`${keyPrefix}${levelKey}HourRangeMin`] = formatValue(item.rangeMin);
        result[`${keyPrefix}${levelKey}HourRangeMax`] = formatValue(item.rangeMax);
        result[`${keyPrefix}${levelKey}HasSecondRange`] = item.hasSecondRange;
        result[`${keyPrefix}${levelKey}SecondHourRangeMin`] = formatValue(item.secondRangeMin || null);
        result[`${keyPrefix}${levelKey}SecondHourRangeMax`] = formatValue(item.secondRangeMax || null);
      } else {
        result[`${keyPrefix}${levelKey}Hours`] = formatValue(item.hour, true);
      }

      for (const location of knownLocations) {
        const locationKey = locationKeys[location];
        result[`${keyPrefix}${levelKey}ServiceLocation${locationKey}`] = item.locationOfService.includes(location);
      }

      result[`${keyPrefix}${levelKey}OtherSettingDescription`] = item.locationOfService.includes(OTHER_SETTING_LOCATION)
        ? item.otherSettingDescription ?? ""
        : "";
    }

    return result;
  } catch (error) {
    throw new Error(`Failed to parse or transform treatment table content: ${error}`);
  }
};

// Docxtemplater is configured with `linebreaks: true` (see useTemplateExport.ts), which splits a
// tag value on "\n" into separate <w:t> runs joined by <w:br/> without trimming either side of the
// split. A stray space next to an embedded line break (e.g. multiple providers joined by "\n") would
// otherwise survive as a visible indent on the line after the break.
const trimLineBreakWhitespace = (content: string): string =>
  content
    .split("\n")
    .map((line) => line.trim())
    .join("\n");

export const processSectionFieldContent = (
  field: ExportSectionFieldType,
  font: string,
  sectionFieldEdges: Array<ReportSectionFieldEdge> = [],
): string | TableData[] | boolean | null | TransformedData | DsmSpecifierRow[] => {
  if (!field.content) {
    console.warn(`processExportData: field "${field.field_identifier}" has no content.`);
    return "";
  }
  const fieldType = field.field_type;
  switch (fieldType) {
    case "SIMPLE_TEXT":
    case "DATE":
    case "DATETIME":
      return trimLineBreakWhitespace(field.content);
    case "IMAGE":
    case "ASSESSMENT_IMAGE":
      return getImageContentBase64FromField(sectionFieldEdges, field.field_identifier as ReportFieldIdentifier) || "";
    case "WYSIWYG":
      return markdownToDocxtemplater(field.content, font);
    case "RADIO":
    case "SELECT":
      return getRadioAndSelectFieldAnswer(field.content);
    case "CHECKBOX":
      return getCheckboxFieldAnswer(field.content);
    case "TABLE":
    case "COORDINATION_OF_CARE":
      return transformTableContent(field.content);
    case "ASSESSMENT_APPOINTMENTS": {
      const tableData = transformTableContent(field.content);
      if (!tableData) return null;
      return tableData.map((row) => ({
        ...row,
        date: row.date && typeof row.date === "string" ? formatDate(row.date) : row.date,
      }));
    }
    case "BOOLEAN":
      return field.content === "true";
    case "DSM_Table":
    case "DSM_TABLE":
      return transformDSMTableContent(field.content);
    case "SPECIFIERS_TABLE": {
      try {
        return JSON.parse(field.content) ?? [];
      } catch {
        console.warn(`processExportData: Failed to parse SPECIFIERS_TABLE content for "${field.field_identifier}".`);
        return [];
      }
    }
    default:
      console.warn(`processExportData: Unhandled field_type "${fieldType}" for key "${field.field_identifier}".`);
      return "";
  }
};

export const processExportData = (
  reportData: ReportNode,
  exportData: ReportExportDataType | undefined | null,
  font: string,
): Record<string, TemplateDataValue> => {
  if (!exportData) {
    throw new Error("No export data provided to processExportData.");
  }
  const processedData: Record<string, TemplateDataValue> = {};

  const mergeStringFields = (
    obj?: Maybe<ReportExportDataType | ExportClientDetailsType | ExportSignatureSectionType>,
  ) => {
    if (!obj) return;
    for (const [key, value] of Object.entries(obj)) {
      if (typeof value === "string") {
        processedData[key] = value;
      }
    }
  };
  mergeStringFields(exportData);
  mergeStringFields(exportData.clientDetails);
  mergeStringFields(exportData.signatureSection);

  //sections
  exportData.sections?.forEach((section) => {
    if (section?.key && section.fields) {
      const exportSection: ExportSection = {
        title: section.title ?? "",
        fields: section.fields
          .filter((field): field is ExportSectionFieldType => field != null)
          .map((field) => {
            const content = processSectionFieldContent(field, font);
            const exportField: ExportSectionField = {
              title: field.title ?? "",
              content,
              field_type: field.field_type ?? "",
              field_identifier: field.field_identifier ?? "",
              confidence_interval_label: getConfidenceIntervalLabel(content),
            };
            return exportField;
          }),
      };
      processedData[section.key] = exportSection;
    }
  });

  //fields
  exportData.sections?.forEach((section) => {
    if (section?.key && section.fields) {
      const sectionFieldEdges = getSectionObjectBySectionIdentifier(
        reportData?.reportSections.edges,
        section.key as ReportSectionIdentifier,
      )?.node?.sectionField.edges;
      section.fields.forEach((field) => {
        if (field?.field_type == "TREATMENT_OPTION_TABLE") {
          Object.assign(
            processedData,
            transformTreatmentTableData(
              field.content ?? null,
              field.field_identifier?.split("_")[0].toLowerCase() || "default",
            ),
          );
        } else if (field?.field_identifier) {
          processedData[`${section.key}_${field.field_identifier}`] = processSectionFieldContent(
            field,
            font,
            sectionFieldEdges,
          );
        }
      });
    }
  });

  // Group goals by goalType
  exportData.goalGroups?.forEach((goalGroup) => {
    if (goalGroup?.goalType && goalGroup?.goals) {
      processedData[goalGroup.goalType] = goalGroup.goals.filter((goal): goal is ExportGoalType => goal != null);
    }
  });

  return processedData;
};
